/**
 * Add Wallet Modal
 * Modern modal for adding new wallets to track
 * Redesigned with glassmorphism effects
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useWalletTrackerStore } from '../../store/walletTrackerStore';
import { alchemyApi } from '../../services/alchemyApi';
import { Icons } from '../Icons';
import { TRACKER_COLORS } from '../../config/monitorColors';
import type { TrackedWallet } from '../../types/wallet.types';

// ============================================
// CONSTANTS & STYLES
// ============================================

// Predefined tags with colors
const PREDEFINED_TAGS = [
  { id: 'smart-money', label: 'Smart Money', emoji: '🧠', color: '#8b5cf6' },
  { id: 'whale', label: 'Whale', emoji: '🐋', color: '#3b82f6' },
  { id: 'defi-pro', label: 'DeFi Pro', emoji: '🏦', color: '#10b981' },
  { id: 'nft-trader', label: 'NFT Trader', emoji: '🎨', color: '#ec4899' },
  { id: 'influencer', label: 'Influencer', emoji: '⭐', color: '#f59e0b' },
  { id: 'fund', label: 'Fund', emoji: '💼', color: '#6366f1' },
  { id: 'personal', label: 'Personal', emoji: '👤', color: '#64748b' },
];

const s = {
  overlay: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 9999,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    backdropFilter: 'blur(8px)',
  },
  modal: {
    maxWidth: '560px',
    width: '95%',
    maxHeight: '90vh',
    overflow: 'auto',
    background: 'linear-gradient(180deg, rgba(15, 23, 42, 0.98), rgba(10, 15, 30, 0.99))',
    borderRadius: '20px',
    border: '1px solid rgba(6, 182, 212, 0.2)',
    boxShadow: '0 24px 64px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.05)',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 24px',
    borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
    background: 'rgba(6, 182, 212, 0.05)',
  },
  body: {
    padding: '24px',
  },
  label: {
    display: 'block',
    fontSize: '13px',
    fontWeight: 600,
    marginBottom: '10px',
    color: 'var(--text)',
    letterSpacing: '0.3px',
  },
  input: (hasError: boolean, isValid: boolean) => ({
    width: '100%',
    padding: '14px 16px',
    fontSize: '14px',
    fontFamily: 'monospace',
    border: hasError
      ? `1px solid ${TRACKER_COLORS.error}`
      : isValid
        ? `1px solid ${TRACKER_COLORS.success}`
        : '1px solid rgba(255, 255, 255, 0.1)',
    borderRadius: '12px',
    background: 'rgba(0, 0, 0, 0.3)',
    color: 'var(--text)',
    outline: 'none',
    transition: 'all 0.2s ease',
  }),
  tagButton: (isSelected: boolean, color: string) => ({
    fontSize: '12px',
    padding: '8px 14px',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    borderRadius: '20px',
    border: 'none',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    background: isSelected ? `${color}25` : 'rgba(255, 255, 255, 0.05)',
    color: isSelected ? color : 'var(--muted)',
    fontWeight: isSelected ? 600 : 400,
    boxShadow: isSelected ? `0 0 12px ${color}30` : 'none',
  }),
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    padding: '20px 24px',
    borderTop: '1px solid rgba(255, 255, 255, 0.06)',
    background: 'rgba(0, 0, 0, 0.2)',
  },
  cancelButton: {
    padding: '12px 20px',
    fontSize: '14px',
    fontWeight: 500,
    background: 'rgba(255, 255, 255, 0.05)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    borderRadius: '10px',
    color: 'var(--text)',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  submitButton: (disabled: boolean) => ({
    padding: '12px 24px',
    fontSize: '14px',
    fontWeight: 600,
    background: disabled
      ? 'rgba(100, 100, 100, 0.3)'
      : `linear-gradient(135deg, ${TRACKER_COLORS.primary}, ${TRACKER_COLORS.secondary})`,
    border: 'none',
    borderRadius: '10px',
    color: disabled ? 'rgba(150, 150, 150, 0.6)' : 'white',
    cursor: disabled ? 'not-allowed' : 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    transition: 'all 0.2s ease',
    boxShadow: disabled ? 'none' : `0 4px 16px ${TRACKER_COLORS.primary}40`,
  }),
};

// ============================================
// UTILITIES
// ============================================

function isValidEthereumAddress(address: string): boolean {
  return /^0x[a-fA-F0-9]{40}$/.test(address);
}

function isENSName(input: string): boolean {
  return input.endsWith('.eth');
}

// ============================================
// COMPONENT
// ============================================

interface AddWalletModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function AddWalletModal({ isOpen, onClose }: AddWalletModalProps) {
  const { t } = useTranslation();
  const { addWallet } = useWalletTrackerStore();

  const [addressInput, setAddressInput] = useState('');
  const [customLabel, setCustomLabel] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [notes, setNotes] = useState('');

  // Validation & Resolution state
  const [isValidating, setIsValidating] = useState(false);
  const [validationError, setValidationError] = useState('');
  const [resolvedAddress, setResolvedAddress] = useState<string | null>(null);
  const [ensName, setEnsName] = useState<string | null>(null);

  // Loading state
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setAddressInput('');
      setCustomLabel('');
      setSelectedTags([]);
      setNotes('');
      setValidationError('');
      setResolvedAddress(null);
      setEnsName(null);
    }
  }, [isOpen]);

  // Validate and resolve address/ENS
  useEffect(() => {
    const validateAddress = async () => {
      if (!addressInput.trim()) {
        setValidationError('');
        setResolvedAddress(null);
        setEnsName(null);
        return;
      }

      setIsValidating(true);
      setValidationError('');

      try {
        if (isENSName(addressInput)) {
          if (!alchemyApi.isAlchemyConfigured()) {
            setValidationError(t('walletTracker.modal.alchemyNotConfigured', 'Alchemy API not configured'));
            setIsValidating(false);
            return;
          }

          const address = await alchemyApi.resolveENSName(addressInput);

          if (!address || address === '0x0000000000000000000000000000000000000000') {
            setValidationError(t('walletTracker.modal.ensNotFound', 'ENS name not found'));
            setResolvedAddress(null);
            setEnsName(null);
          } else {
            setResolvedAddress(address);
            setEnsName(addressInput);
            setValidationError('');
          }
        } else if (isValidEthereumAddress(addressInput)) {
          setResolvedAddress(addressInput);
          setEnsName(null);
          setValidationError('');
        } else {
          setValidationError(t('walletTracker.modal.invalidAddress', 'Invalid address format'));
          setResolvedAddress(null);
          setEnsName(null);
        }
      } catch (error) {
        console.error('Address validation error:', error);
        setValidationError(t('walletTracker.modal.validationError', 'Validation error'));
        setResolvedAddress(null);
      } finally {
        setIsValidating(false);
      }
    };

    const timeoutId = setTimeout(validateAddress, 500);
    return () => clearTimeout(timeoutId);
  }, [addressInput, t]);

  const toggleTag = (tagId: string) => {
    setSelectedTags(prev =>
      prev.includes(tagId)
        ? prev.filter(t => t !== tagId)
        : [...prev, tagId]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!resolvedAddress) {
      setValidationError(t('walletTracker.modal.enterValidAddress', 'Please enter a valid address'));
      return;
    }

    setIsSubmitting(true);

    try {
      const wallet: TrackedWallet = {
        address: resolvedAddress,
        label: customLabel || ensName || `${resolvedAddress.slice(0, 6)}...${resolvedAddress.slice(-4)}`,
        ensName: ensName || undefined,
        tags: selectedTags,
        addedAt: new Date().toISOString(),
        chain: 'ethereum',
        notes: notes || undefined,
      };

      addWallet(wallet);
      onClose();
    } catch (error) {
      console.error('Error adding wallet:', error);
      setValidationError(t('walletTracker.modal.addError', 'Failed to add wallet'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const quickAddWallet = (address: string, label: string, tags: string[] = []) => {
    setAddressInput(address);
    setCustomLabel(label);
    setSelectedTags(tags);
  };

  if (!isOpen) {
    return null;
  }

  const isFormValid = resolvedAddress && !validationError && !isSubmitting;

  return (
    <div style={s.overlay} onClick={onClose}>
      <div style={s.modal} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={s.header}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '12px',
                background: `linear-gradient(135deg, ${TRACKER_COLORS.primary}, ${TRACKER_COLORS.secondary})`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Icons.Wallet size={20} color="white" />
            </div>
            <div>
              <h3 style={{ fontSize: '18px', fontWeight: 600, margin: 0, color: 'var(--text)' }}>
                {t('walletTracker.modal.title', 'Add Wallet')}
              </h3>
              <p style={{ fontSize: '13px', color: 'var(--muted)', margin: 0 }}>
                {t('walletTracker.modal.subtitle', 'Track any Ethereum wallet')}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              padding: '8px',
              background: 'rgba(255, 255, 255, 0.05)',
              border: 'none',
              borderRadius: '8px',
              color: 'var(--muted)',
              cursor: 'pointer',
            }}
          >
            <Icons.X size={18} />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit}>
          <div style={s.body}>
            {/* Address Input */}
            <div style={{ marginBottom: '24px' }}>
              <label style={s.label}>
                <Icons.Hash size={14} style={{ display: 'inline', marginRight: '6px', opacity: 0.6 }} />
                {t('walletTracker.modal.addressLabel', 'Ethereum Address or ENS')}
              </label>
              <input
                type="text"
                value={addressInput}
                onChange={(e) => setAddressInput(e.target.value)}
                placeholder="0x... or vitalik.eth"
                style={s.input(!!validationError, !!resolvedAddress && !validationError)}
                autoFocus
              />

              {/* Validation Feedback */}
              <div style={{ marginTop: '10px', minHeight: '24px' }}>
                {isValidating && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    fontSize: '12px',
                    color: TRACKER_COLORS.primary,
                  }}>
                    <Icons.Loader size={14} style={{ animation: 'spin 1s linear infinite' }} />
                    {t('walletTracker.modal.validating', 'Validating...')}
                  </div>
                )}
                {validationError && !isValidating && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    fontSize: '12px',
                    color: TRACKER_COLORS.error,
                    padding: '8px 12px',
                    background: `${TRACKER_COLORS.error}15`,
                    borderRadius: '8px',
                  }}>
                    <Icons.AlertCircle size={14} />
                    {validationError}
                  </div>
                )}
                {resolvedAddress && !validationError && !isValidating && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    fontSize: '12px',
                    color: TRACKER_COLORS.success,
                    padding: '8px 12px',
                    background: `${TRACKER_COLORS.success}15`,
                    borderRadius: '8px',
                  }}>
                    <Icons.CheckCircle size={14} />
                    {t('walletTracker.modal.validAddress', 'Valid address')}
                    {ensName && (
                      <span style={{ color: 'var(--muted)', marginLeft: '4px' }}>
                        ({ensName})
                      </span>
                    )}
                  </div>
                )}
              </div>

              {/* Resolved Address Display */}
              {resolvedAddress && ensName && (
                <div style={{
                  marginTop: '10px',
                  padding: '12px 14px',
                  background: 'rgba(6, 182, 212, 0.08)',
                  borderRadius: '10px',
                  border: '1px solid rgba(6, 182, 212, 0.15)',
                  fontSize: '12px',
                  fontFamily: 'monospace',
                  color: 'var(--muted)',
                }}>
                  <span style={{ color: TRACKER_COLORS.primary, fontWeight: 500 }}>Address:</span>{' '}
                  {resolvedAddress.slice(0, 20)}...{resolvedAddress.slice(-8)}
                </div>
              )}
            </div>

            {/* Custom Label */}
            <div style={{ marginBottom: '24px' }}>
              <label style={s.label}>
                <Icons.Edit size={14} style={{ display: 'inline', marginRight: '6px', opacity: 0.6 }} />
                {t('walletTracker.modal.labelLabel', 'Custom Label')}
                <span style={{ fontWeight: 400, opacity: 0.5, marginLeft: '6px' }}>
                  ({t('common.optional', 'Optional')})
                </span>
              </label>
              <input
                type="text"
                value={customLabel}
                onChange={(e) => setCustomLabel(e.target.value)}
                placeholder={ensName || t('walletTracker.modal.labelPlaceholder', 'My wallet label')}
                style={{
                  ...s.input(false, false),
                  fontFamily: 'inherit',
                }}
              />
            </div>

            {/* Tags Selection */}
            <div style={{ marginBottom: '24px' }}>
              <label style={s.label}>
                <Icons.Layers size={14} style={{ display: 'inline', marginRight: '6px', opacity: 0.6 }} />
                {t('walletTracker.modal.tagsLabel', 'Tags')}
              </label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {PREDEFINED_TAGS.map(tag => (
                  <button
                    key={tag.id}
                    type="button"
                    onClick={() => toggleTag(tag.id)}
                    style={s.tagButton(selectedTags.includes(tag.id), tag.color)}
                  >
                    <span>{tag.emoji}</span>
                    <span>{tag.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Notes */}
            <div style={{ marginBottom: '24px' }}>
              <label style={s.label}>
                <Icons.FileText size={14} style={{ display: 'inline', marginRight: '6px', opacity: 0.6 }} />
                {t('walletTracker.modal.notesLabel', 'Notes')}
                <span style={{ fontWeight: 400, opacity: 0.5, marginLeft: '6px' }}>
                  ({t('common.optional', 'Optional')})
                </span>
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder={t('walletTracker.modal.notesPlaceholder', 'Add any notes about this wallet...')}
                rows={3}
                style={{
                  ...s.input(false, false),
                  fontFamily: 'inherit',
                  resize: 'vertical',
                  minHeight: '80px',
                }}
              />
            </div>

            {/* Quick Add Popular Wallets */}
            <div style={{
              paddingTop: '20px',
              borderTop: '1px solid rgba(255, 255, 255, 0.06)',
            }}>
              <div style={{
                fontSize: '12px',
                fontWeight: 600,
                color: 'var(--muted)',
                marginBottom: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
              }}>
                <Icons.Zap size={14} style={{ color: TRACKER_COLORS.warning }} />
                {t('walletTracker.modal.quickAdd', 'Quick add popular wallets')}
              </div>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {[
                  { address: 'vitalik.eth', label: 'Vitalik Buterin', emoji: '🏛️', tags: ['influencer', 'smart-money'] },
                  { address: '0x28C6c06298d514Db089934071355E5743bf21d60', label: 'Binance Hot', emoji: '🏦', tags: ['whale', 'fund'] },
                  { address: '0x47ac0Fb4F2D84898e4D9E7b4DaB3C24507a6D503', label: 'Binance 8', emoji: '💎', tags: ['whale'] },
                ].map((wallet) => (
                  <button
                    key={wallet.address}
                    type="button"
                    onClick={() => quickAddWallet(wallet.address, wallet.label, wallet.tags)}
                    style={{
                      fontSize: '11px',
                      padding: '6px 12px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      background: 'rgba(255, 255, 255, 0.05)',
                      border: '1px solid rgba(255, 255, 255, 0.08)',
                      borderRadius: '8px',
                      color: 'var(--text)',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'rgba(6, 182, 212, 0.1)';
                      e.currentTarget.style.borderColor = 'rgba(6, 182, 212, 0.2)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                      e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.08)';
                    }}
                  >
                    <span>{wallet.emoji}</span>
                    <span>{wallet.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div style={s.footer}>
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              style={s.cancelButton}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
              }}
            >
              {t('common.cancel', 'Cancel')}
            </button>
            <button
              type="submit"
              disabled={!isFormValid}
              style={s.submitButton(!isFormValid)}
            >
              {isSubmitting ? (
                <>
                  <Icons.Loader size={16} style={{ animation: 'spin 1s linear infinite' }} />
                  {t('common.adding', 'Adding...')}
                </>
              ) : (
                <>
                  <Icons.Plus size={16} />
                  {t('walletTracker.modal.addWallet', 'Add Wallet')}
                </>
              )}
            </button>
          </div>
        </form>

        {/* Spin Animation */}
        <style>{`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    </div>
  );
}
